import { notification, Modal } from 'antd';
import { AppContext } from 'App.context';
import { useContext, useEffect, useState } from 'react';
import { ApiContext } from 'utils/networking/Api.context';
import { ExclamationCircleOutlined } from '@ant-design/icons';

const LANGUAGE_ORDER = ['en', 'es', 'de', 'ca', 'fr', 'pt', 'it', 'jp', 'cn', 'ru', 'nl'];

export default function useFormsHook() {
  const { API } = useContext(ApiContext);
  const { appState, appActions } = useContext(AppContext);
  const { translate, getSelectedHotel } = appActions;
  const changeFormVersionModal = Modal.confirm;

  const [state, setState] = useState({
    userCanEdit: appState.role !== 'guest',
    isLoading: false,
    confirmFormVersionModal: false,
    showCopyFormConfigModal: false,
    languages: null,
    selectedLanguage: null,
    selectedformVersion: null,
    form: null,
    isDynamicForm: false,
    pmsModuleName: null,
    pmsHasDynamicForms: false,
    selectedFieldName: '',
    copyFormConfigHotelList: [],
    copyErrorMessage: null,
  });

  useEffect(() => {
    actions.setSelectedHotel(getSelectedHotel());
    actions.fetchPmsConfig(getSelectedHotel().key);
  }, [getSelectedHotel()?.key]);

  const actions = {
    showCopyFormConfigModal: value => {
      updateState({ showCopyFormConfigModal: value });
    },

    copyFormConfig: (hotelId, onSuccessClearHotels) => {
      API.copyFormConfig({ hotelId, data: state.copyFormConfigHotelList })
        .then(res => {
          if (res.data.failedCopies.length > 0) {
            updateState({
              copyErrorMessage:
                translate('guestTable.copyFormConfigPartialError') +
                res.data.failedCopies.join(', '),
            });
          } else {
            updateState({
              showCopyFormConfigModal: false,
              copyFormConfigHotelList: [],
              copyErrorMessage: null,
            });
            if (typeof onSuccessClearHotels === 'function') {
              onSuccessClearHotels();
            }
            notification.success({
              message: translate('guestTable.copyFormConfigSuccess'),
            });
          }
        })
        .catch(err => {
          notification.error({
            message: translate('guestTable.error'),
          });
        });
    },

    setHotelToCopyFormConfig: hotelSelectedList => {
      updateState({ copyFormConfigHotelList: hotelSelectedList });
    },

    onLanguageSelected: option => {
      updateState({ selectedLanguage: option });
    },

    onFormVersionSelected: option => {
      updateState({ confirmFormVersionModal: true });

      changeFormVersionModal({
        open: state.confirmFormVersionModal,
        title: translate('guestTable.formVersionChangeTitle'),
        icon: <ExclamationCircleOutlined />,
        content: translate('guestTable.formVersionChangeConfirmation'),
        okText: translate('confirm'),
        okButtonProps: {
          disabled: state.isLoading,
        },
        cancelText: translate('cancel'),
        cancelButtonProps: {
          disabled: state.isLoading,
        },
        onOk() {
          const oldFormVersionSelected = state.selectedformVersion;

          updateState({ isLoading: true });
          API.setFormsVersion(getSelectedHotel().id, option)
            .then(res => {
              actions.loadGuestFormData(getSelectedHotel().id);
            })
            .catch(err => {
              updateState({ selectedformVersion: oldFormVersionSelected, isLoading: false });
            });
        },
        onCancel() {
          updateState({ confirmFormVersionModal: false });
        },
      });
    },

    orderLanguages: languages => {
      return LANGUAGE_ORDER.reduce((ordered, lang) => {
        const found = languages.find(l => l.code === lang);
        if (found) ordered.push(found);
        return ordered;
      }, []);
    },

    setSelectedHotel: hotel => {
      if (hotel && hotel?.key != 'null') {
        actions.loadGuestFormData(hotel.key);
      }
    },

    loadGuestFormData: hotelId => {
      updateState({ isLoading: true, form: null });

      API.guestFormsRequest(hotelId)
        .then(res => {
          const parsedFormItems = res.isDynamicForm
            ? actions.getSortedFormByGuest(res?.hotelFields)
            : actions.parseFormItems(res?.hotelFields);
          const languages = actions.orderLanguages(res?.languages);

          // if (res.isDynamicForm) {
          //   for (let [key, value] of Object.entries(parsedFormItems || {})) {
          //     const guestNationalityField = value.find(item => item.name === 'guest_nationality');
          //     const nationalityField = value.find(item => item.name === 'nationality');

          //     if (nationalityField && guestNationalityField) {
          //       const { name: gName, ...gRest } = guestNationalityField;
          //       const { name: nName, ...nRest } = nationalityField;

          //       Object.assign(guestNationalityField, { ...nRest, name: gName });
          //       Object.assign(nationalityField, { ...gRest, name: nName });
          //     }
          //   }
          // }

          updateState({
            isLoading: false,
            confirmFormVersionModal: false,
            form: parsedFormItems,
            languages: languages,
            isDynamicForm: res.isDynamicForm,
            selectedFieldName: res.dynamicFormBaseField,
            selectedformVersion: res.formVersion,
          });

          if (
            !state.selectedLanguage ||
            !languages.map(lang => lang.code).includes(state.selectedLanguage)
          ) {
            actions.onLanguageSelected(languages[0].code);
          }
        })
        .catch(err => {
          updateState({ isLoading: false });
          console.warn(err);
        });
    },

    parseFormItems: formItems => {
      return formItems.map(item => {
        if (item.onlyItaly) {
          if (item.name === 'province' || item.name === 'city') {
            item.desc = translate('guestTable.italyResidencyDesc');
          } else {
            item.desc = translate('guestTable.italyDesc');
          }
          return item;
        } else return item;
      });
    },

    getSortedFormByGuest: hotelFields => {
      return {
        holder_1: hotelFields['holder_1'],
        holder_0: hotelFields['holder_0'],
        adult_1: hotelFields['adult_1'],
        adult_0: hotelFields['adult_0'],
        child_1: hotelFields['child_1'],
        child_0: hotelFields['child_0'],
      };
    },

    saveForm: (formData, applyToAllHotels, selectedFieldName) => {
      const customFields = JSON.parse(
        JSON.stringify(state.isDynamicForm ? formData.fields : formData)
      ).filter(formItem => formItem?.id);

      const hotelFields = state.isDynamicForm
        ? actions.getFieldsFromDynamicForm(formData.fields, formData.modifiedFieldHotels)
        : JSON.parse(JSON.stringify(formData)).filter(formItem => formItem?.fieldHotelId);

      const formattedFields = customFields.map(field => {
        delete field?.id;
        delete field?.key;
        delete field?.defaultTexts;
        if (!field.customText) delete field?.formTexts;

        return field;
      });

      const customTranslations = state.isDynamicForm ? formData.customTranslations : null;

      updateState({ isLoading: true });
      API.updateGuestFormRequest({
        hotelId: !applyToAllHotels ? getSelectedHotel().key : null,
        data: formattedFields,
        hotelFields,
        customTranslations,
        dynamicFormBaseField: selectedFieldName,
      })
        .then(res => {
          updateState({ isLoading: false });
          notification.success({
            message: translate('guestTable.success'),
          });
        })
        .catch(err => {
          console.warn(err);
          updateState({ isLoading: false });
          notification.error({
            message: translate('guestTable.error'),
          });
        });
    },

    getFieldsFromDynamicForm: (form, modifiedFieldHotels) => {
      const hotelFields = form.reduce((acc, field) => {
        if (field.values) {
          const filteredValues = field.values.filter(value =>
            modifiedFieldHotels.includes(value.fieldHotelId)
          );
          acc.push(...filteredValues);
        }
        return acc;
      }, []);

      return hotelFields;
    },

    fetchPmsConfig: async hotelId => {
      try {
        const config = await API.pmsConfigDynamicFormByHotelIdRequest({ hotelId });

        updateState({
          pmsModuleName: config.name,
          pmsHasDynamicForms: config?.dynamic_forms === 1,
        });
      } catch (error) {
        console.error('Error loading PMS:', error);
      }
    },

    saveBaseFieldSelected: fieldName => {
      updateState({ selectedFieldName: fieldName });
    },
  };

  function updateState(object) {
    setState(previousState => ({ ...previousState, ...object }));
  }

  return { state, actions };
}
